"use client"
import { fontWeights } from "@/utils/fonts";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";

interface QuestionsProps {
  title?: string;
  subtitle?: string;
  items?: any[];
  locationName?: string;
  serviceName?: string;
}

export default function Questions({
  title,
  subtitle,
  items,
  locationName,
  serviceName
}: QuestionsProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const replaceDynamicPlaceholders = (text?: string) => {
    if (!text) return "";
    return text
      .replace(/{location}/g, locationName || "")
      .replace(/{service}/g, serviceName || "");
  };

  const finalTitle = replaceDynamicPlaceholders(title);
  const finalSubtitle = replaceDynamicPlaceholders(subtitle);
  const displayItems = items && items.length > 0 ? items : [];

  return (
    <div>
      <div className="text-center">
        <h2
          className="
          sm:text-[24px]
          lg:text-[35px]
          2xl:text-[40px]
          3xl:text-[45px]
          h-auto
          tsm-350:h-[30px]
          sm:h-[40px]
          lg:h-[54px]
          max-h-[150px]
          w-auto
          tsm-350:w-[300px]
          sm:w-[600px]
          2xl:w-[813px]
          3xl:w-[963px]
          max-w-[963px]
          px-0
          sm:px-8
          mx-auto
          text-[#000000]
          "
          style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.semiBold }}
          dangerouslySetInnerHTML={{ __html: finalTitle }}
        />
        {finalSubtitle && (
          <p
            className="
            leading-none
            sm:leading-[22px]
            lg:leading-[26px]
            my-[8px]
            sm:my-[15px]
            lg:my-[25px]
            2xl:my-[31px] 
            text-[12px]
            lg:text-[15px]
            2xl:text-[18px]
            h-auto
            max-h-full
            px-[20px]
            sm:px-2
            lg:px-0
            w-auto
            sm:w-[500px]
            md:w-[670px]
            lg:w-[800px]
            2xl:lg:w-[968px]
            max-w-full
            mx-auto
            text-[#000000]
            "
            style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.regular, lineHeight: '1.3' }}
          >
            {finalSubtitle}
          </p>
        )}
      </div>
      <div className="px-4 sm:px-0 mx-auto h-full w-full
         max-w-full 
         sm:max-w-[450px] 
         md:max-w-[700px] 
         lg:max-w-[920px] 
         xl:max-w-[1120px] 
         2xl:max-w-[1320px]
         mb-[10px] 
         sm:mb-[20px] 
         md:mb-[30px] 
         lg:mb-[40px] 
         xl:mb-[50px] 
         2xl:mb-[63px]
         ">
        <div className="grid grid-cols-1 md:grid-cols-2   
         gap-[5px] 
         md:gap-[10px] 
         lg:gap-[14px] 
         xl:gap-[22px] 
         2xl:gap-[30px]       
         sm:gap-x-[15px] 
         md:gap-x-[20px] 
         lg:gap-x-[30px] 
         xl:gap-x-[40px] 
         2xl:gap-x-[60px]
         ">
          {displayItems.map((item, index) => (
            <div
              key={index}
              className="border border-[#002F6D] flex flex-col justify-between relative overflow-hidden transition-all duration-300   
           p-3 
           tsm-400:p-4 
           md:p-2 
           lg:p-4 
           w-full 
           sm:w-[450px] 
           md:w-[340px] 
           lg:w-[448px] 
           xl:w-[540px] 
           2xl:w-[630px]   
           h-full 
           tsm-350:h-[110px] 
           md:h-[120px] 
           lg:h-[140px] 
           xl:h-[160px] 
           2xl:h-[180px]
           "
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <div className="flex items-center">
                <h3 className="text-[#000000]
               text-[14px] lg:text-[18px] xl:text-[20px] 2xl:text-[24px]
                 "
                  style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.semiBold, lineHeight: "1.2" }}
                >{replaceDynamicPlaceholders(item.question)}</h3>
                {hoverIndex === index ? <Minus className="text-[#000000] absolute bottom-3 lg:bottom-4 right-3 lg:right-4 w-[15px] lg:w-auto h-[15px] lg:h-auto" /> : <Plus className="text-[#000000] absolute bottom-3 lg:bottom-4 right-3 lg:right-4 w-[15px] lg:w-auto h-[15px] lg:h-auto" />}
              </div>
              <div className={`transition-opacity duration-300 ${hoverIndex === index ? 'opacity-100 h-auto' : 'opacity-0 h-0'}`}>
                <p className="text-[#002F6D] 
             leading-[1.2] 
             md:leading-[1.3] 
             xl:leading-[1.4] 
             2xl:leading-[1.1]        
             px-[6px] 
             md:px-[8px] 
             lg:px-[13px] 
             xl:px-[25px] 
             2xl:px-[30px]    
             pb-2 
             sm:pb-[9px] 
             lg:pb-[12px] 
             xl:pb-[22px] 
             2xl:pb-[30px]  
             pt-2 
             sm:pt-[8px] 
             md:pt-[10px] 
             lg:pt-[15px] 
             xl:pt-[20px] 
             2xl:pt-[25px]  
             text-[12px] 
             sm:text-[12px] 
             lg:text-[14px] 
             xl:text-[15px] 
             2xl:text-[16px]
               "
                  style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.medium }}
                >
                  {replaceDynamicPlaceholders(item.answer)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className=" lg:py-[70px] sm:py-[50px] tsm-350:py-[30px] py-[25px]">
        <div className="lg:h-[1px] h-[0.99px] lg:w-[200px] w-[150px] bg-[#002F6D] mx-auto" />
      </div>
    </div>
  );
}
